import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LockResetOutlined } from '@mui/icons-material';
import { toast } from '../../ui/toast';
import { AUTH_KEYS, NS } from '../../i18n/constants/keys';
import { TextInput } from '../../ui/input/text_input/text_input';
import { confirmPasswordReset } from '../../utils/api_service/invite_api';
import { RootState } from '../../utils/redux_store/store';
import { FormInput } from '../../utils/types/login_register_types';
import { AuthenticationUiLayout } from './AuthenticationUILayout';
import { CompanyLogoBadge } from './CompanyLogoBadge';
import { validate, validateForm } from './validation/validation_rules';

export const ChangePassword = () => {
  const history = useHistory();
  const { t } = useTranslation(NS.auth);
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState<FormInput>({
    email: localStorage.getItem('email') || 'sample@hiacuity.com',
    password: '',
    confirmPassword: '',
    errors: {
      emailError: '',
      passwordError: '',
      confirmPasswordError: '',
    },
  });
  const [formValid, setFormValid] = useState<boolean>(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let errors = validate(event.target, password);

    setPassword({
      ...password,
      [event.target.name]: event.target.value,
      errors: errors,
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let isSubmittedFormValid = validateForm(password);
    if (isSubmittedFormValid) {
      confirmPasswordReset(token, password.password)
        .then((response) => {
          if (response.ok) {
            setFormValid(true);
            history.push('/login');
            toast.success(t(AUTH_KEYS.changePassword.success), {
              position: 'bottom-center',
            });
          } else {
            return response.json().then((data) => {
              toast.error(data.message || 'This reset link is no longer valid', { position: 'bottom-center' });
            });
          }
        })
        .catch((error) => toast.error(error, { position: 'bottom-center' }));
    } else {
      toast.error(t(AUTH_KEYS.login.invalidDetails), {
        position: 'bottom-center',
      });
      setFormValid(false);
    }
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  return (
    <AuthenticationUiLayout
      leftContent={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
            textAlign: 'center',
            gap: 3,
            color: '#ffffff',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '24px',
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(16px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <LockResetOutlined sx={{ fontSize: 40, color: '#34d399' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>
              {t(AUTH_KEYS.changePassword.leftTitle)}
            </Typography>
            <Typography sx={{ color: 'rgba(236, 253, 245, 0.8)', fontSize: '1rem', maxWidth: 300, mx: 'auto', lineHeight: 1.5 }}>
              {t(AUTH_KEYS.changePassword.leftSubtitle)}
            </Typography>
          </Box>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', gap: 2.5 }}>
        <CompanyLogoBadge
          logoUrl={companyLogo}
          companyName={companyName}
          altText='HiAcuity Logo'
        />
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.875rem' },
              fontWeight: 800,
              color: '#0f172a',
              letterSpacing: '-0.02em',
            }}
          >
            {t(AUTH_KEYS.changePassword.title)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            {t(AUTH_KEYS.changePassword.subtitle)}
          </Typography>
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        data-testid="change-password-form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 4,
          width: '100%',
        }}
      >
        <TextInput
          type="password"
          label={t(AUTH_KEYS.changePassword.password)}
          data-testid="password"
          name="password"
          variant="pill"
          placeholder="New password"
          value={password.password}
          onChange={handleChange}
          required
          error={!formValid && Boolean(password.errors.passwordError)}
          helperText={!formValid ? password.errors.passwordError : undefined}
        />

        <TextInput
          type="password"
          label={t(AUTH_KEYS.changePassword.confirmPassword)}
          data-testid="confirmPassword"
          name="confirmPassword"
          variant="pill"
          placeholder="Confirm new password"
          value={password.confirmPassword || ''}
          onChange={handleChange}
          required
          error={!formValid && Boolean(password.errors.confirmPasswordError)}
          helperText={!formValid ? password.errors.confirmPasswordError : undefined}
        />

        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ mt: 2, width: '100%' }}>
          <Button
            fullWidth
            type="button"
            variant="outlined"
            onClick={() => history.goBack()}
            sx={{
              borderRadius: '9999px',
              py: 1.25,
              borderColor: '#e2e8f0',
              color: '#475569',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
              },
            }}
          >
            {t(AUTH_KEYS.changePassword.cancel)}
          </Button>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              borderRadius: '9999px',
              py: 1.25,
              bgcolor: '#050508',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.16)',
              '&:hover': {
                bgcolor: '#1e293b',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.22)',
              },
            }}
          >
            {t(AUTH_KEYS.changePassword.changeButton)}
          </Button>
        </Stack>
      </Box>
    </AuthenticationUiLayout>
  );
};
